import { Component, computed, inject, signal, OnInit, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { MeterEditData } from '../state/bill-ai-flow.models';
import { BillFile } from '../../../models';
import { LegalEntityMatchResult, ServiceAddressMatchResult, MeterMatchResult, BillMatchResult, DuplicateWarning, OverlapType, MappedAccountNumbers, MatchSuggestion } from '../../../models/bill-import.models';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { OrganizationManagementService } from '../../../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/organization-management.service';
import { MatrixPricingDataService } from '../../../../zentility/_modules/zen-market-pulse/_services/matrix-pricing-data.service';
import { BillImportService } from '../../../services/bill-import.service';
import { BillService } from '../../../services/bill.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ItemListV4Model } from '../../../../zentility/_shared/_model/item-list-v4.model';
import { take } from 'rxjs/operators';
import { formatBillDate, formatBillPeriod } from '../../../utils/date-format.util';
import { getScoreClass } from '../../../utils/match-score.util';
import { cleanIdentifierName, genericUanLabel } from '../../../utils/bill-utils';
import { EntityMatchSelectorComponent } from '../entity-match-selector/entity-match-selector.component';
import { ScoreSelectComponent, ScoreSelectOption } from '../score-select/score-select.component';
import { DuplicateResolutionModalComponent } from '../duplicate-resolution-modal/duplicate-resolution-modal.component';
import { ParentContextBannerComponent } from '../parent-context-banner/parent-context-banner.component';
import { ParentConflictResolverComponent } from '../parent-context-banner/parent-conflict-resolver.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MergeModalComponent } from '../../merge-modal/merge-modal.component';
import { SelectionActionBarComponent } from '../../selection-action-bar/selection-action-bar.component';
import { validateMeterStep, MeterValidationInput } from './bill-ai-flow-meter-step.validation';
import { StepValidationResult, ValidationSummary, buildFieldErrorMaps } from '../validation';

/**
 * Pure helper for the meter-step header's rate class cell. Returns:
 *   - `''` when nothing is selected (header shows "Not set")
 *   - the formatted list entry when the id resolves (success path)
 *   - `"Rate Class {id}"` + `warn=true` when the id can't be resolved in the
 *     loaded list (stale cache or race — don't render blank, so the user knows
 *     *something* is selected even if the display is degraded).
 */
export function computeRateClassLabel(
  id: number | null | undefined,
  items: ItemListV4Model[] | undefined
): { label: string; warn: boolean } {
  if (id == null) return { label: '', warn: false };
  const match = items?.find(rc => rc.key === id);
  if (match) return { label: match.value, warn: false };
  return { label: `Rate Class ${id}`, warn: true };
}

@Component({
  selector: 'app-bill-ai-flow-meter-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    SharedModule,
    EntityMatchSelectorComponent,
    ScoreSelectComponent,
    DuplicateResolutionModalComponent,
    ParentContextBannerComponent,
    ParentConflictResolverComponent,
    MatCheckboxModule,
    MergeModalComponent,
    SelectionActionBarComponent
  ],
  templateUrl: './bill-ai-flow-meter-step.component.html',
  styleUrl: './bill-ai-flow-meter-step.component.scss'
})
export class BillAiFlowMeterStepComponent implements OnInit {
  // Expose enum to template
  readonly CommodityType = CommodityType;

  // Pre-computed values for template
  readonly state = inject(BillAiFlowStore);
  private orgMgtSvc = inject(OrganizationManagementService);
  private matrixPricingDataSvc = inject(MatrixPricingDataService);
  private billImportSvc = inject(BillImportService);
  private billService = inject(BillService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  /**
   * Tracks the most recent utilityId the user chose per meter, so the late
   * response from a previous utility pick doesn't overwrite state for a newer
   * utility pick. See remapUansForUtility() for the guard.
   */
  private inFlightRemapUtility = new Map<string, number>();
  legalEntities = computed(() => this.state.matchResponse()?.legalEntities ?? []);
  isNewCustomer = this.state.isNewCustomer;

  /**
   * Meter groupIds that Express Import flagged as blocking fast-track on this
   * step — ambiguous/low-confidence meter matches and meters with blocking
   * duplicate bills. Template applies `.needs-attention` to these rows.
   * Empty unless all three hold: fast-track mode is 'fast', the meter step
   * wasn't marked skipped during execution, and the analyzer recorded
   * blockers here.
   */
  fastTrackBlockingSet = computed<Set<string>>(() => {
    if (this.state.fastTrackMode() !== 'fast') return new Set();
    if (this.state.fastTrackSkippedSteps().includes('meter')) return new Set();
    const ids = this.state.fastTrackAnalysis()?.blockingGroupIds?.['meter'] ?? [];
    return new Set(ids);
  });

  /** When parents are known (SA or METER level), hide LEN/SA group headers and show meters flat */
  showHierarchy = computed(() => {
    const ctx = this.state.getContext();
    return !ctx || ctx.level === 'PORTFOLIO' || ctx.level === 'CUSTOMER' || ctx.level === 'LEN';
  });

  // Track which meters are currently in edit mode
  editingMeters = signal<Set<string>>(new Set());
  hoveredMeter: string | null = null;

  // Utility dropdown data, keyed by `${state}|${commodity}` — utility lists are
  // commodity-filtered, so elec and gas meters in one state need separate lists.
  utilityItemLists = new Map<string, ItemListV4Model[]>();
  utilityListsLoading = new Set<string>();
  // Commodities whose utility fetch failed. Nothing is cached for them (so a retry is
  // possible), but rebuildLookupMaps() must not refetch on every pass — that would loop.
  utilityListsFailed = new Set<CommodityType>();

  // Rate class dropdown data (per utility)
  rateClassLists = new Map<number, ItemListV4Model[]>();
  rateClassLoading = new Set<number>();

  // Duplicate resolution modal state
  showDuplicateModal = signal(false);

  // Entity mismatch warnings (pre-computed, keyed by groupId)
  lenMismatchExpected: Record<string, string> = {};
  saMismatchExpected: Record<string, string> = {};
  // Original extracted names (before overrides) for mismatch display
  private originalExtractedNames: Record<string, string> = {};

  // Inline editing state
  editingLenName: string | null = null;

  // Pre-computed lookup maps for template (avoids method calls in templates)
  meterAccountNumbers: Record<string, { uan1: string; uan2: string; uan3: string }> = {};
  meterCommodityTypes: Record<string, CommodityType> = {};
  meterCommodityTypeLabels: Record<string, string> = {};
  meterEditDataMap: Record<string, MeterEditData> = {};
  meterUtilities: Record<string, ItemListV4Model[]> = {};
  /** Display name for the meter's currently-selected utility, used by the
   *  read-only summary row. Falls back to the match-response utility name
   *  when the user hasn't picked one yet. */
  meterUtilityLabels: Record<string, string> = {};
  meterRateClasses: Record<string, ItemListV4Model[]> = {};
  meterRateClassLabels: Record<string, string> = {};
  meterUanOptionsMap: Record<string, ScoreSelectOption[]> = {};
  meterUanOptionsWithNoneMap: Record<string, ScoreSelectOption[]> = {};
  meterUanLabels: Record<string, Record<number, string>> = {};
  billLabels: Record<string, string> = {};
  /** Pair display info per merged bill groupId: source-document labels + the supplier half's file. */
  billPairInfo: Record<string, { supplierFile: BillFile; supplierLabel: string; utilityLabel: string }> = {};
  /** Expanded state of the source-bill rows per merged bill groupId. */
  billPairExpanded: Record<string, boolean> = {};
  /** Physical document count per meter (a paired bill counts as two documents). */
  meterBillCounts: Record<string, number> = {};
  billDuplicateBadgeLabels: Record<string, string> = {};
  billDuplicateTooltips: Record<string, string> = {};
  billDuplicateIcons: Record<string, string> = {};
  billDuplicateBlocking: Record<string, boolean> = {};
  billDuplicatePairable: Record<string, boolean> = {};
  // Pre-computed maps for template (avoids method calls in nested @for loops)
  lenChildCountMap: Record<string, string> = {};
  saChildSummaryMap: Record<string, string> = {};
  originalNameMap: Record<string, string> = {};
  meterIsNewMap: Record<string, boolean> = {};
  meterIsForcedNewMap: Record<string, boolean> = {};
  /**
   * Blocked-note location per meter groupId: set when the meter's account
   * number already exists under a DIFFERENT service address (cross-entity
   * duplicate). Value is the existing meter's location ("LEN · address") or
   * null when the backend didn't provide one.
   */
  meterExistsElsewhereMap: Record<string, string | null> = {};
  /** Cross-entity duplicate per meter groupId — the row is fully blocked. */
  meterBlockedMap: Record<string, boolean> = {};
  /** Any blocked row on this step — gates Continue (precomputed for the template). */
  anyMeterBlocked = false;
  /** Exact in-scope UAN hit — linked to the existing meter, Create New disabled. */
  meterExactLockedMap: Record<string, boolean> = {};
  /** FYI: this account number exists on another customer in the org. */
  meterSeenOnCustomerMap: Record<string, string | null> = {};
  meterUanMap: Record<string, string> = {};
  editingMeterSet: Set<string> = new Set();
  mergeSelectedMap: Record<string, boolean> = {};
  previewingBillMap: Record<string, boolean> = {};
  blockingDuplicateError: string | null = null;

  // --- Validation state (see guides/ai-onboarding/FIELD-VALIDATION.md) ---
  fieldErrors: Record<string, Record<string, boolean>> = {};
  hasEntityErrors: Record<string, boolean> = {};
  fieldWarnings: Record<string, Record<string, boolean>> = {};
  validationSummary: ValidationSummary = { errorGroups: [], warningGroups: [], totalErrors: 0, totalWarnings: 0 };

  // UAN validation rules from utility_account_number table (per meter, per position)
  uanValidation: Record<string, Record<number, { regex?: RegExp; label: string; errorMessage: string; required: boolean }>> = {};
  uanValidationErrors: Record<string, Record<number, string>> = {};

  // Merge modal state
  mergeSelection = this.state.meterMergeSelection;
  mergeModalState: {
    isOpen: boolean;
    candidates: string[];
    labelMap: Record<string, string>;
    subLabelMap: Record<string, string>;
  } = {
    isOpen: false, candidates: [], labelMap: {}, subLabelMap: {}
  };
  canUndoMerge = this.state.canUndoMeterMerge;
  canUndoDelete = this.state.canUndoMeterDelete;

  constructor() {
    // Rebuild pre-computed maps when match data changes
    effect(() => {
      // saEdits is read here because the SA's state drives the meter's
      // utility list and UAN validation rules — an SA-step edit must
      // re-derive both here. entitySelections drives new-vs-existing
      // gating (duplicate badges, exists-elsewhere notes).
      this.state.matchRequest();
      this.state.matchResponse();
      this.state.meterEdits();
      this.state.saEdits();
      this.state.entitySelections();
      this.rebuildLookupMaps();
      this.revalidate();
    });

    // Track which bill is currently being previewed
    effect(() => {
      const previewId = this.state.previewBillFileId();
      const mapping = this.state.billFileIdMapping();
      const map: Record<string, boolean> = {};
      for (const [groupId, fileId] of Object.entries(mapping)) {
        map[groupId] = fileId === previewId;
      }
      this.previewingBillMap = map;
    });

    // Track editing meters signal changes
    effect(() => {
      this.editingMeterSet = this.editingMeters();
    });
    // Track merge selection changes
    effect(() => {
      const selection = this.mergeSelection();
      const response = this.state.matchResponse();
      if (response) {
        for (const len of response.legalEntities) {
          for (const sa of len.serviceAddresses) {
            for (const meter of sa.meters) {
              this.mergeSelectedMap[meter.groupId] = selection.includes(meter.groupId);
            }
          }
        }
      }
    });
  }

  ngOnInit(): void {
    this.initAllMeters();
    this.computeMismatchWarnings();
    this.rebuildLookupMaps();
  }

  /** Rebuild all pre-computed lookup maps for template binding */
  private rebuildLookupMaps(): void {
    const request = this.state.matchRequest();
    const response = this.state.matchResponse();
    if (!request) return;

    // Build meter account numbers and commodity types from request
    for (const len of request.legalEntities) {
      for (const sa of len.serviceAddresses) {
        // Effective state: saEdits wins so a user edit on the SA step
        // re-keys the utility list here.
        const effectiveState = this.state.getSaEdit(sa.groupId)?.state || sa.state || '';
        for (const m of sa.meters) {
          this.meterAccountNumbers[m.groupId] = {
            uan1: m.utilityAccountNumber1 || '',
            uan2: m.utilityAccountNumber2 || '',
            uan3: m.utilityAccountNumber3 || ''
          };
          const ct = m.commodityType || CommodityType.Electricity;
          this.meterCommodityTypes[m.groupId] = ct;
          this.meterCommodityTypeLabels[m.groupId] = ct === CommodityType.Gas ? 'Gas' : 'Electricity';
          this.meterEditDataMap[m.groupId] = this.state.getMeterEdit(m.groupId);
          const utilityListKey = this.utilityListKey(effectiveState, ct);
          this.meterUtilities[m.groupId] = this.utilityItemLists.get(utilityListKey) || [];
          // Kick off a fetch for the new state's utility list if we don't
          // have it yet — the rebuild will re-run when the response lands.
          if (effectiveState && !this.utilityItemLists.has(utilityListKey)) {
            this.loadUtilitiesForState(effectiveState, ct);
          }
          this.meterRateClasses[m.groupId] = this.meterEditDataMap[m.groupId]?.utilityId
            ? (this.rateClassLists.get(this.meterEditDataMap[m.groupId].utilityId!) || [])
            : [];
          this.updateUtilityLabel(m.groupId);
          this.updateRateClassLabel(m.groupId);

          // UAN options
          const uanOpts = this.getUanOptions(m.groupId);
          this.meterUanOptionsMap[m.groupId] = uanOpts;
          this.meterUanOptionsWithNoneMap[m.groupId] = [{ value: '', label: '-- None --' }, ...uanOpts];

          // UAN labels
          this.meterUanLabels[m.groupId] = {};
          for (const pos of [1, 2, 3] as const) {
            this.meterUanLabels[m.groupId][pos] = this.getUanLabel(m.groupId, pos);
          }
        }
      }
    }

    // Build bill labels, duplicate info, and hierarchy summary from response
    if (response) {
      // Rebuilt from scratch — selection toggles can remove entries, and
      // per-key assignment would leave stale ones behind.
      this.billDuplicateBadgeLabels = {};
      this.billDuplicateTooltips = {};
      this.billDuplicateIcons = {};
      this.billDuplicateBlocking = {};
      this.billDuplicatePairable = {};
      this.billPairInfo = {};
      this.meterBillCounts = {};
      const pairByUtilityFileId = new Map<string, { utilityFile: BillFile; supplierFile: BillFile }>();
      for (const pair of this.billService.pairedGroups()) {
        if (pair.utilityBill && pair.supplierBill) {
          pairByUtilityFileId.set(pair.utilityBill.id, { utilityFile: pair.utilityBill, supplierFile: pair.supplierBill });
        }
      }
      const fileIdMapping = this.state.billFileIdMapping();
      this.meterExistsElsewhereMap = {};
      this.meterBlockedMap = {};
      this.anyMeterBlocked = false;
      this.meterExactLockedMap = {};
      this.meterSeenOnCustomerMap = {};
      for (const len of response.legalEntities) {
        this.lenChildCountMap[len.groupId] = this.getLenChildCount(len);
        this.originalNameMap[len.groupId] = this.state.getOriginalName(len.groupId);
        for (const sa of len.serviceAddresses) {
          this.saChildSummaryMap[sa.groupId] = this.getSaChildSummary(sa);
          this.originalNameMap[sa.groupId] = this.state.getOriginalName(sa.groupId);
          const isForcedNew = this.isMeterForcedNew(len.groupId, sa.groupId);
          this.meterIsForcedNewMap[`${len.groupId}__${sa.groupId}`] = isForcedNew;
          for (const meter of sa.meters) {
            const meterIsNew = isForcedNew || this.state.getSelection(meter.groupId) === null;
            this.meterIsNewMap[`${len.groupId}__${sa.groupId}__${meter.groupId}`] = meterIsNew;
            // Cross-entity duplicate: account number exists under a different
            // SA — the row is blocked (JenZen error note shows where it lives).
            const blocked = !!meter.crossParentMatch;
            this.meterBlockedMap[meter.groupId] = blocked;
            if (blocked) this.anyMeterBlocked = true;
            if (blocked) {
              this.meterExistsElsewhereMap[meter.groupId] =
                [meter.existingLenName?.trim(), meter.existingSaLabel?.trim()].filter(Boolean).join(' · ') || null;
            }
            // Exact in-scope hit: linked to the existing meter; creating a
            // duplicate account number is never offered.
            this.meterExactLockedMap[meter.groupId] =
                !blocked && !isForcedNew && !!meter.match && meter.match.score >= 1;
            this.meterSeenOnCustomerMap[meter.groupId] = meter.seenOnCustomerName ?? null;
            this.meterUanMap[meter.groupId] = this.state.getOriginalName(meter.groupId);
            // Seed the utility display label from the response so the
            // read-only summary has something to show before utility lists
            // load. Only seed when the user's current pick still matches
            // the response — otherwise we'd clobber a user-changed label
            // every time meterEdits changes (rebuild effect re-runs on
            // every meter-edit signal write). initAllMeters() pre-fills
            // utilityId from the response, so the initial-load case
            // satisfies the equality check.
            if (
              meter.utility?.utilityId &&
              meter.utility?.utilityName &&
              this.meterEditDataMap[meter.groupId]?.utilityId === meter.utility.utilityId
            ) {
              this.meterUtilityLabels[meter.groupId] = meter.utility.utilityName;
            }
            // Store UAN validation rules from mapped account numbers (initial load)
            if (meter.utility?.mappedAccountNumbers) {
              this.storeUanValidationRules(meter.groupId, meter.utility.mappedAccountNumbers);
              this.validateUanFields(meter.groupId);
            }
            for (const bill of meter.bills) {
              this.billLabels[bill.groupId] = this.getBillLabel(bill);
              const pairFiles = pairByUtilityFileId.get(fileIdMapping[bill.groupId]);
              if (pairFiles) {
                const u = pairFiles.utilityFile.data;
                const s = pairFiles.supplierFile.data;
                const uDates = formatBillPeriod(u?.billingPeriodStart, u?.billingPeriodEnd);
                const sDates = formatBillPeriod(s?.billingPeriodStart, s?.billingPeriodEnd);
                const uName = u?.utilityName || u?.providerName;
                this.billPairInfo[bill.groupId] = {
                  supplierFile: pairFiles.supplierFile,
                  utilityLabel: uName ? `${uName}: ${uDates}` : uDates,
                  supplierLabel: s?.providerName ? `${s.providerName}: ${sDates}` : sDates
                };
              }
              // No duplicate badges on new meters — see computeNewMeterBillGroupIds.
              if (bill.duplicate && !meterIsNew) {
                // A declined pair suggestion renders as a plain blocking overlap.
                const dup = bill.duplicate.pairable && this.state.isPairRejected(bill.groupId)
                  ? { ...bill.duplicate, pairable: false }
                  : bill.duplicate;
                this.billDuplicateBadgeLabels[bill.groupId] = this.getDuplicateBadgeLabel(dup);
                this.billDuplicateTooltips[bill.groupId] = this.getDuplicateTooltip({ ...bill, duplicate: dup });
                this.billDuplicateIcons[bill.groupId] = this.getDuplicateIcon(dup);
                this.billDuplicateBlocking[bill.groupId] = this.isBlockingDuplicate(dup);
                this.billDuplicatePairable[bill.groupId] = !!dup.pairable;
              }
            }
            this.meterBillCounts[meter.groupId] =
              meter.bills.length + meter.bills.filter(b => this.billPairInfo[b.groupId]).length;
          }
        }
      }
    }
  }

  /** Refresh a single meter's edit data map entry (call after field changes) */
  refreshMeterEditData(groupId: string): void {
    this.meterEditDataMap[groupId] = this.state.getMeterEdit(groupId);
    this.meterRateClasses[groupId] = this.meterEditDataMap[groupId]?.utilityId
      ? (this.rateClassLists.get(this.meterEditDataMap[groupId].utilityId!) || [])
      : [];
    this.updateUtilityLabel(groupId);
    this.updateRateClassLabel(groupId);
  }

  private updateUtilityLabel(groupId: string): void {
    const id = this.meterEditDataMap[groupId]?.utilityId;
    if (id == null) {
      // User has no selection — drop any prior label so the summary row
      // shows "Unknown Utility" instead of a stale name.
      delete this.meterUtilityLabels[groupId];
      return;
    }
    const items = this.meterUtilities[groupId] || [];
    const match = items.find(u => u.key === id);
    if (match) {
      this.meterUtilityLabels[groupId] = match.value;
    }
    // If the list hasn't loaded yet, leave the response-seeded label in
    // place — it'll be overwritten on the next refresh after the list loads.
  }

  private updateRateClassLabel(groupId: string): void {
    const id = this.meterEditDataMap[groupId]?.rateClassId;
    const items = this.meterRateClasses[groupId];
    const result = computeRateClassLabel(id, items);
    this.meterRateClassLabels[groupId] = result.label;
    if (result.warn) {
      console.warn(
        '[BillAiFlowMeterStep] rateClassId', id, 'not found in loaded list for meter', groupId
      );
    }
  }

  private computeMismatchWarnings(): void {
    const context = this.state.getContext();
    if (!context) return;
    const normalize = (s: string | undefined | null): string => (s || '').trim().toLowerCase();
    const request = this.state.matchRequest();
    if (!request) return;

    // At SERVICE_ADDRESS or METER level: compare extracted LEN against known LEN
    if ((context.level === 'SERVICE_ADDRESS' || context.level === 'METER') && context.lenName) {
      for (const len of request.legalEntities) {
        const extractedRaw = len.name || 'Unknown';
        const extracted = normalize(extractedRaw);
        this.originalExtractedNames[len.groupId] = extractedRaw;
        if (extracted && extracted !== normalize(context.lenName) && extracted !== 'unknown entity' && extracted !== 'unknown') {
          this.lenMismatchExpected[len.groupId] = context.lenName;
        }
      }
    }

    // At METER level: also compare extracted SA against known SA
    if (context.level === 'METER' && context.serviceAddress) {
      const knownAddr = [context.serviceAddress.street1, context.serviceAddress.city, context.serviceAddress.state]
        .filter(Boolean).map(s => normalize(s)).join(', ');
      for (const len of request.legalEntities) {
        for (const sa of len.serviceAddresses) {
          const extractedRaw = [sa.street1, sa.city, sa.state].filter(Boolean).join(', ') || 'Unknown';
          const extracted = normalize(extractedRaw);
          this.originalExtractedNames[sa.groupId] = extractedRaw;
          if (extracted && extracted !== knownAddr && extracted !== 'unknown address' && extracted !== 'unknown') {
            this.saMismatchExpected[sa.groupId] = [context.serviceAddress.street1, context.serviceAddress.city, context.serviceAddress.state].filter(Boolean).join(', ');
          }
        }
      }
    }
  }

  /** Get the original extracted name from the match request (ignoring overrides) */
  getOriginalExtractedLenName(groupId: string): string {
    return this.originalExtractedNames[groupId] || 'Unknown';
  }

  /** Apply the parent (context) LEN name to this entity */
  useParentLenName(groupId: string): void {
    const expected = this.lenMismatchExpected[groupId];
    if (expected) {
      this.state.updateEntityName(groupId, expected);
      delete this.lenMismatchExpected[groupId];
    }
  }

  /** Save inline-edited LEN name */
  saveLenName(groupId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const newName = input.value.trim();
    if (newName) {
      this.state.updateEntityName(groupId, newName);
      // Re-check mismatch
      const context = this.state.getContext();
      if (context?.lenName) {
        const normalize = (s: string) => s.trim().toLowerCase();
        if (normalize(newName) === normalize(context.lenName)) {
          delete this.lenMismatchExpected[groupId];
        }
      }
    }
    this.editingLenName = null;
  }

  /** Initialize edit data and load utilities for all meters upfront */
  private initAllMeters(): void {
    const request = this.state.matchRequest();
    const response = this.state.matchResponse();
    if (!request) return;

    for (const len of request.legalEntities) {
      for (const sa of len.serviceAddresses) {
        for (const meter of sa.meters) {
          this.state.initMeterEditFromRequest(meter.groupId);

          // If the backend resolved a utility ID in the match response, use it
          if (response) {
            const respMeter = this.findMeterInResponse(response, meter.groupId);
            if (respMeter?.utility?.utilityId) {
              const edit = this.state.getMeterEdit(meter.groupId);
              if (!edit.utilityId) {
                this.state.updateMeterEdit(meter.groupId, { utilityId: respMeter.utility.utilityId });
              }
            }
          }

          this.loadUtilitiesForMeter(meter.groupId);
        }
      }
    }
  }

  /** Find a meter in the match response by groupId */
  private findMeterInResponse(response: any, groupId: string): MeterMatchResult | null {
    for (const len of response.legalEntities || []) {
      for (const sa of len.serviceAddresses || []) {
        for (const meter of sa.meters || []) {
          if (meter.groupId === groupId) return meter;
        }
      }
    }
    return null;
  }

  // --- Cascade Rules ---

  isMeterForcedNew(lenGroupId: string, saGroupId: string): boolean {
    return this.isNewCustomer() || this.state.isNewEntity(lenGroupId) || this.state.isNewEntity(saGroupId);
  }

  isMeterNew(lenGroupId: string, saGroupId: string, meterGroupId: string): boolean {
    return this.isMeterForcedNew(lenGroupId, saGroupId)
      || this.state.getSelection(meterGroupId) === null;
  }

  // --- Match Selection ---

  onSelectionChange(groupId: string, value: number | string | null): void {
    this.state.updateSelection(groupId, value);
  }

  // --- Meter Edit Handlers ---

  onMeterFieldChange(groupId: string, field: keyof MeterEditData, value: any): void {
    // A rate class belongs to a utility: switching to a different utility drops the
    // seeded/selected one (the import rejects a schedule from another utility).
    const utilityChanged = field === 'utilityId' && this.state.getMeterEdit(groupId).utilityId !== value;
    const patch: Partial<MeterEditData> = utilityChanged
      ? { utilityId: value, rateClassId: null, rateClassUserSet: false }
      : field === 'rateClassId'
        ? { rateClassId: value, rateClassUserSet: true }
        : { [field]: value };
    this.state.updateMeterEdit(groupId, patch);
    this.refreshMeterEditData(groupId);

    // When utility changes, reload rate classes and re-assign UANs
    if (field === 'utilityId' && value) {
      this.loadRateClasses(value);
      // Drop the old utility's rules synchronously so validateUanFields
      // doesn't flag values against a stale regex while the remap is in flight.
      delete this.uanValidation[groupId];
      this.uanValidationErrors[groupId] = {};
      this.validateUanFields(groupId);
      this.remapUansForUtility(groupId, value);
    }

    // Re-validate UAN fields on manual change
    if (field === 'utilityAccountNumber1' || field === 'utilityAccountNumber2' || field === 'utilityAccountNumber3') {
      this.validateUanFields(groupId);
    }
  }

  /**
   * Re-assign UAN1/2/3 when the utility changes, using the server's
   * utility_account_number table (accountOrder + validationExpression).
   *
   * Stale-response guard: each call stamps `inFlightRemapUtility` with the
   * requested utility id. When the response arrives we only apply it if the
   * user hasn't picked a different utility in the meantime — otherwise a
   * quick A→B switch could let A's late response clobber B's state.
   */
  private remapUansForUtility(meterGroupId: string, utilityId: number): void {
    // Collect additionalIdentifiers from all bills for this meter.
    // First value per identifier name wins across bills — the utility_account_number
    // mapping is a pure (name → slot) lookup, so later duplicates wouldn't
    // change the result.
    const identifiers: Record<string, string> = {};
    const response = this.state.matchResponse();
    if (response) {
      const meter = this.findMeterInResponse(response, meterGroupId);
      if (meter?.bills) {
        for (const bill of meter.bills) {
          const billFile = this.state.getBillFileForGroup(bill.groupId);
          if (billFile?.data?.additionalIdentifiers) {
            for (const ident of billFile.data.additionalIdentifiers) {
              if (ident.name && ident.value && !(ident.name in identifiers)) {
                identifiers[ident.name] = ident.value;
              }
            }
          }
        }
      }
    }

    if (Object.keys(identifiers).length === 0) {
      console.warn('[BillAiFlowMeterStep] Skipping UAN remap — no additionalIdentifiers for meter', meterGroupId);
      return;
    }

    this.inFlightRemapUtility.set(meterGroupId, utilityId);

    const commodityType = this.meterEditDataMap[meterGroupId]?.commodityType || CommodityType.Electricity;
    this.billImportSvc.mapIdentifiers({ utilityId, commodityType, additionalIdentifiers: identifiers })
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          // Drop stale responses — user already moved to a different utility.
          if (this.inFlightRemapUtility.get(meterGroupId) !== utilityId) {
            return;
          }
          this.inFlightRemapUtility.delete(meterGroupId);

          if (!result?.mappedAccountNumbers) {
            console.warn('[BillAiFlowMeterStep] No mappedAccountNumbers returned for utility', utilityId, 'meter', meterGroupId);
            // Clear stale rules so the step-level required rule on UAN1 takes over.
            delete this.uanValidation[meterGroupId];
            this.uanValidationErrors[meterGroupId] = {};
            this.validateUanFields(meterGroupId);
            this.snackBar.open(
              'No identifier mapping available for this utility — you can still fill the account numbers manually.',
              'Dismiss',
              { duration: 6000 }
            );
            return;
          }

          const mapped = result.mappedAccountNumbers;
          this.state.updateMeterEdit(meterGroupId, {
            utilityAccountNumber1: mapped.utilityAccountNum1?.value || '',
            utilityAccountNumber2: mapped.utilityAccountNum2?.value || '',
            utilityAccountNumber3: mapped.utilityAccountNum3?.value || ''
          });
          this.meterAccountNumbers[meterGroupId] = {
            uan1: mapped.utilityAccountNum1?.value || '',
            uan2: mapped.utilityAccountNum2?.value || '',
            uan3: mapped.utilityAccountNum3?.value || ''
          };
          this.storeUanValidationRules(meterGroupId, mapped);
          this.validateUanFields(meterGroupId);

          this.refreshMeterEditData(meterGroupId);
          this.uanOptionsCache.delete(meterGroupId);
          this.meterUanOptionsMap[meterGroupId] = this.getUanOptions(meterGroupId);
          this.meterUanOptionsWithNoneMap[meterGroupId] = [{ value: '', label: '-- None --' }, ...this.meterUanOptionsMap[meterGroupId]];
        },
        error: (err) => {
          // Only surface if this is still the current request for this meter
          // (avoids a stacked snackbar barrage when the user cycles utilities).
          if (this.inFlightRemapUtility.get(meterGroupId) === utilityId) {
            this.inFlightRemapUtility.delete(meterGroupId);
          }
          console.error('[BillAiFlowMeterStep] Failed to remap identifiers for utility', utilityId, 'meter', meterGroupId, err);
          this.snackBar.open(
            'Couldn\'t update account numbers for this utility. Please try again, or fill them in manually.',
            'Dismiss',
            { duration: 6000 }
          );
        }
      });
  }

  /**
   * Store per-position validation rules (regex, label, required flag) from the
   * server's mappedAccountNumbers block. Wraps new RegExp() in try/catch —
   * `validationExpression` is server-controlled and a bad pattern would
   * otherwise throw synchronously and tear down the entire ngOnInit loop.
   */
  private storeUanValidationRules(meterGroupId: string, mapped: MappedAccountNumbers): void {
    this.uanValidation[meterGroupId] = {};
    const positions: Array<{ pos: 1 | 2 | 3; mapping: MappedAccountNumbers['utilityAccountNum1'] }> = [
      { pos: 1, mapping: mapped.utilityAccountNum1 },
      { pos: 2, mapping: mapped.utilityAccountNum2 },
      { pos: 3, mapping: mapped.utilityAccountNum3 }
    ];
    for (const { pos, mapping } of positions) {
      if (!mapping) continue;

      let regex: RegExp | undefined;
      if (mapping.validationExpression) {
        try {
          regex = new RegExp(mapping.validationExpression);
        } catch (err) {
          console.warn(
            '[BillAiFlowMeterStep] Bad validationExpression for meter', meterGroupId,
            'position', pos, 'pattern', mapping.validationExpression, err
          );
          regex = undefined; // keep label/required; skip regex check
        }
      }

      this.uanValidation[meterGroupId][pos] = {
        regex,
        label: mapping.label || `Account Number ${pos}`,
        errorMessage: mapping.label ? `Invalid ${mapping.label} format` : 'Invalid format',
        required: mapping.required ?? (pos === 1)
      };
    }
  }

  /**
   * Validate current UAN values against stored rules, set warnings.
   *
   * When no rules are stored (initial load before match response, or a
   * utility-change remap that's still in flight), fall back to the
   * step-level "UAN1 required" rule so the blocker behaviour doesn't
   * silently disappear while the async remap is mid-flight.
   */
  private validateUanFields(meterGroupId: string): void {
    const edit = this.meterEditDataMap[meterGroupId];
    if (!this.fieldWarnings[meterGroupId]) this.fieldWarnings[meterGroupId] = {};
    if (!this.uanValidationErrors[meterGroupId]) this.uanValidationErrors[meterGroupId] = {};

    const fields = {
      1: 'utilityAccountNumber1',
      2: 'utilityAccountNumber2',
      3: 'utilityAccountNumber3'
    } as const;
    const values = {
      1: edit?.utilityAccountNumber1 || '',
      2: edit?.utilityAccountNumber2 || '',
      3: edit?.utilityAccountNumber3 || ''
    };

    const rules = this.uanValidation[meterGroupId];

    for (const pos of [1, 2, 3] as const) {
      const field = fields[pos];
      const value = values[pos];
      const rule = rules?.[pos];

      // Always clear prior warning/error state so stale flags don't persist.
      this.fieldWarnings[meterGroupId][field] = false;
      this.uanValidationErrors[meterGroupId][pos] = '';

      if (!rule) {
        // No per-utility rule yet — only UAN1 has a step-level required rule;
        // UAN2/3 are optional without a utility to pin them against.
        if (pos === 1 && !value) {
          this.fieldWarnings[meterGroupId][field] = true;
          this.uanValidationErrors[meterGroupId][pos] = `${genericUanLabel(1)} is required`;
        }
        continue;
      }

      if (rule.required && !value) {
        this.fieldWarnings[meterGroupId][field] = true;
        this.uanValidationErrors[meterGroupId][pos] = `${rule.label} is required`;
      } else if (value && rule.regex && !rule.regex.test(value)) {
        this.fieldWarnings[meterGroupId][field] = true;
        this.uanValidationErrors[meterGroupId][pos] = rule.errorMessage;
      }
    }
  }

  // --- Utility Loading ---

  private loadUtilitiesForMeter(meterGroupId: string): void {
    const request = this.state.matchRequest();
    if (!request) return;

    // Find the SA's state for this meter
    for (const len of request.legalEntities) {
      for (const sa of len.serviceAddresses) {
        for (const meter of sa.meters) {
          if (meter.groupId === meterGroupId) {
            const saState = sa.state || '';
            const meterCt = meter.commodityType === CommodityType.Gas ? CommodityType.Gas : CommodityType.Electricity;
            if (saState && !this.utilityItemLists.has(this.utilityListKey(saState, meterCt))) {
              this.loadUtilitiesForState(saState, meterCt);
            }
            // Also load rate classes if meter already has a utility
            const edit = this.state.getMeterEdit(meterGroupId);
            if (edit.utilityId) {
              this.loadRateClasses(edit.utilityId);
            }
            return;
          }
        }
      }
    }
  }

  private utilityListKey(stateCode: string, commodityType: CommodityType): string {
    return `${stateCode}|${commodityType}`;
  }

  private loadUtilitiesForState(stateCode: string, commodityType: CommodityType): void {
    const key = this.utilityListKey(stateCode, commodityType);
    // One request serves every state for the commodity, so the in-flight guard is per
    // commodity: two states' meters seen before the response lands must not fire it twice.
    const loadingKey: string = commodityType;
    if (this.utilityListsLoading.has(loadingKey) || this.utilityListsFailed.has(commodityType)) return;
    this.utilityListsLoading.add(loadingKey);

    this.orgMgtSvc.getStateUtilitiesByCommodity(commodityType)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.utilityListsLoading.delete(loadingKey);
          this.utilityListsFailed.delete(commodityType);
          // The endpoint returns EVERY state's utilities for the commodity —
          // cache them all so other states in the session don't refetch.
          for (const su of res || []) {
            if (!su?.stateId) continue;
            const items: ItemListV4Model[] = (su.utilities || [])
              .map(u => ({ value: u.utilityName, key: u.utilityId }));
            this.utilityItemLists.set(this.utilityListKey(su.stateId, commodityType), items);
          }
          if (!this.utilityItemLists.has(key)) {
            this.utilityItemLists.set(key, []);
          }
          this.rebuildLookupMaps();
        },
        error: (err) => {
          console.error('[BillAiFlowMeterStep] Failed to load utilities for state', stateCode, 'commodity', commodityType, err);
          this.utilityListsLoading.delete(loadingKey);
          // Cache nothing: an empty list would look like "no utilities" and block any retry.
          this.utilityListsFailed.add(commodityType);
          this.rebuildLookupMaps();
        }
      });
  }

  // --- Rate Class Loading ---

  private loadRateClasses(utilityId: number): void {
    if (this.rateClassLists.has(utilityId) || this.rateClassLoading.has(utilityId)) return;
    this.rateClassLoading.add(utilityId);

    this.matrixPricingDataSvc.getRateScheduleListWithDescriptions(utilityId)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.rateClassLoading.delete(utilityId);
          const items: ItemListV4Model[] = (res?.rateSchedules || [])
            .map(r => {
              const description = r.description?.trim();
              return { key: r.id, value: description ? `${r.code} - ${description}` : r.code };
            });
          this.rateClassLists.set(utilityId, items);
          this.refreshRateClassesForUtility(utilityId);
        },
        error: (err) => {
          // Intentionally skip `rateClassLists.set(utilityId, [])` — caching an empty
          // array would make the `has(utilityId)` guard short-circuit future retries.
          console.error('[BillAiFlowMeterStep] Failed to load rate classes for utility', utilityId, err);
          this.rateClassLoading.delete(utilityId);
          // Re-run the per-meter label computation so any meter whose store still
          // carries a `rateClassId` renders the degraded "Rate Class {id}" label
          // (via `computeRateClassLabel`'s warn branch) instead of a blank "Not set".
          this.refreshRateClassesForUtility(utilityId);
          this.snackBar.open(
            'Couldn\'t load rate classes for this utility. Please try again.',
            'Dismiss',
            { duration: 6000 }
          );
        }
      });
  }

  // The per-meter `utilityId === utilityId` check below is the stale-response guard:
  // if the user changes utility mid-flight, the late response for the old utility
  // won't touch any meter whose current selection has moved on.
  private refreshRateClassesForUtility(utilityId: number): void {
    const items = this.rateClassLists.get(utilityId) || [];
    for (const groupId of Object.keys(this.meterEditDataMap)) {
      if (this.meterEditDataMap[groupId]?.utilityId === utilityId) {
        this.meterRateClasses[groupId] = items;
        this.updateRateClassLabel(groupId);
      }
    }
  }

  // --- Display Helpers ---

  getMeterUan(meter: MeterMatchResult): string {
    return this.state.getOriginalName(meter.groupId);
  }

  // --- UAN Dropdown Options ---

  /** Collects all available identifiers for a meter from its bills' parsed data */
  getUanOptions(meterGroupId: string): ScoreSelectOption[] {
    const cached = this.uanOptionsCache.get(meterGroupId);
    if (cached) return cached;

    const options: ScoreSelectOption[] = [];
    const seenValues = new Set<string>();

    // Get mapped account numbers from the match response
    const response = this.state.matchResponse();
    if (response) {
      const respMeter = this.findMeterInResponse(response, meterGroupId);
      if (respMeter?.utility?.mappedAccountNumbers) {
        const mapped = respMeter.utility.mappedAccountNumbers;
        for (const key of ['utilityAccountNum1', 'utilityAccountNum2', 'utilityAccountNum3'] as const) {
          const mapping = mapped[key];
          if (mapping?.value && !seenValues.has(mapping.value)) {
            seenValues.add(mapping.value);
            options.push({
              value: mapping.value,
              label: mapping.value,
              badge: mapping.label || key,
            });
          }
        }
      }
    }

    // Get additional identifiers from all bills associated with this meter
    const matchResp = this.state.matchResponse();
    if (matchResp) {
      const respMeter = this.findMeterInResponse(matchResp, meterGroupId);
      if (respMeter?.bills) {
        for (const bill of respMeter.bills) {
          const billFile = this.state.getBillFileForGroup(bill.groupId);
          if (billFile?.data?.additionalIdentifiers) {
            for (const ident of billFile.data.additionalIdentifiers) {
              if (ident.value && !seenValues.has(ident.value)) {
                seenValues.add(ident.value);
                const pct = ident.confidence ? Math.round(ident.confidence * 100) : undefined;
                options.push({
                  value: ident.value,
                  label: ident.value,
                  badge: ident.name,
                  score: pct,
                  scoreClass: pct != null ? getScoreClass(pct) : undefined,
                });
              }
            }
          }
        }
      }
    }

    this.uanOptionsCache.set(meterGroupId, options);
    return options;
  }

  getUanOptionsWithNone(meterGroupId: string): ScoreSelectOption[] {
    return [{ value: '', label: '-- None --' }, ...this.getUanOptions(meterGroupId)];
  }


  /**
   * Get a descriptive label for a UAN field position.
   *
   * Priority:
   *   1. Match response's `meter.utility.mappedAccountNumbers` (populated after a
   *      user-driven utility remap via `mapIdentifiers`).
   *   2. Canonical label persisted on the bill itself during AI extraction
   *      (`utilityAccountNumber[2|3]Label`). Covers the initial render before
   *      any remap, because BillAiImportMatchService.matchUtility currently
   *      doesn't pass additionalIdentifiers and so the match response lands
   *      without mappedAccountNumbers.
   *   3. AI-extracted verbatim name from `additionalIdentifiers` — the AI
   *      recorded exactly what the bill printed ("ESI ID", "Customer Key"),
   *      which is usually close enough for display.
   *   4. Generic "UAN 1" / "UAN 2" / "UAN 3" fallback via genericUanLabel.
   */
  getUanLabel(meterGroupId: string, position: 1 | 2 | 3): string {
    const response = this.state.matchResponse();
    if (response) {
      const respMeter = this.findMeterInResponse(response, meterGroupId);
      const mapped = respMeter?.utility?.mappedAccountNumbers;
      if (mapped) {
        const key = `utilityAccountNum${position}` as keyof typeof mapped;
        if (mapped[key]?.label) {
          return mapped[key].label;
        }
      }

      // The UAN value at this slot was assigned by the backend from one of
      // the meter's bills. Prefer a bill whose own slot value matches the
      // edited value — otherwise the label could be wrong after a remap
      // (e.g. bill A carries "ESIID"/value OLD while the user has typed NEW
      // in the editor, so we should fall through to additionalIdentifiers).
      if (respMeter?.bills) {
        const editValue = position === 1
          ? this.meterEditDataMap[meterGroupId]?.utilityAccountNumber1
          : position === 2
            ? this.meterEditDataMap[meterGroupId]?.utilityAccountNumber2
            : this.meterEditDataMap[meterGroupId]?.utilityAccountNumber3;
        for (const bill of respMeter.bills) {
          const data = this.state.getBillFileForGroup(bill.groupId)?.data;
          if (!data) continue;
          const billSlotValue = position === 1
            ? data.utilityAccountNumber
            : position === 2
              ? data.utilityAccountNumber2
              : data.utilityAccountNumber3;
          const canonical = position === 1
            ? data.utilityAccountNumberLabel
            : position === 2
              ? data.utilityAccountNumber2Label
              : data.utilityAccountNumber3Label;
          // Only trust the canonical label if this bill's slot value still
          // matches what the editor has — guards against post-remap drift.
          if (canonical && canonical.trim() && billSlotValue && billSlotValue === editValue) {
            return canonical;
          }
          // Additional-identifier match is value-keyed, so it's safe regardless
          // of slot position on this particular bill.
          if (editValue && data.additionalIdentifiers) {
            const match = data.additionalIdentifiers.find(id => id.value === editValue);
            if (match?.name) {
              return cleanIdentifierName(match.name);
            }
          }
        }
      }
    }
    return genericUanLabel(position);
  }

  private uanOptionsCache = new Map<string, ScoreSelectOption[]>();

  getBillLabel(bill: BillMatchResult): string {
    const billFile = this.state.getBillFileForGroup(bill.groupId);
    if (billFile?.data) {
      const start = billFile.data.billingPeriodStart;
      const end = billFile.data.billingPeriodEnd;
      const utility = billFile.data.utilityName || '';
      const dates = formatBillPeriod(start, end);
      return utility ? `${utility}: ${dates}` : dates;
    }
    return bill.groupId;
  }


  getSaChildSummary(sa: ServiceAddressMatchResult): string {
    let meterCount = 0;
    let billCount = 0;
    for (const meter of sa.meters) {
      meterCount++;
      billCount += meter.bills.length;
    }
    return `${meterCount} meter${meterCount !== 1 ? 's' : ''}, ${billCount} bill${billCount !== 1 ? 's' : ''}`;
  }

  getLenChildCount(len: LegalEntityMatchResult): string {
    const saCount = len.serviceAddresses.length;
    let meterCount = 0;
    for (const sa of len.serviceAddresses) {
      meterCount += sa.meters.length;
    }
    return `${saCount} address${saCount !== 1 ? 'es' : ''}, ${meterCount} meter${meterCount !== 1 ? 's' : ''}`;
  }

  // --- Duplicate Helpers ---

  isBlockingDuplicate(duplicate: DuplicateWarning): boolean {
    if (duplicate.pairable) return false;
    return duplicate.overlapType === 'PARTIAL' ||
           duplicate.overlapType === 'CONTAINS' ||
           duplicate.overlapType === 'CONTAINED_BY';
  }

  getDuplicateIcon(duplicate: DuplicateWarning): string {
    if (duplicate.pairable) {
      return 'link';
    }
    if (this.isBlockingDuplicate(duplicate)) {
      return 'block';
    }
    return 'warning';
  }

  getDuplicateBadgeLabel(duplicate: DuplicateWarning): string {
    if (duplicate.pairable) {
      return 'Pairs with existing bill';
    }
    switch (duplicate.overlapType) {
      case 'EXACT':
      case 'NEAR_EXACT':
        return 'Already imported';
      case 'PARTIAL':
      case 'CONTAINS':
      case 'CONTAINED_BY':
        return 'Overlapping';
      default: return 'Already imported';
    }
  }

  getDuplicateTooltip(bill: BillMatchResult): string {
    if (!bill.duplicate) return '';
    const dup = bill.duplicate;
    const dates = `${formatBillDate(dup.existingStartDate)} – ${formatBillDate(dup.existingEndDate)}`;

    if (dup.pairable) {
      const half = dup.pairSide === 'DISTRIBUTION' ? 'utility' : 'supplier';
      return `This looks like the ${half} half of existing bill #${dup.existingBillId} (${dates}). It will be merged into that bill on import.`;
    }
    if (this.isBlockingDuplicate(dup)) {
      return `This bill's dates overlap with an existing bill (${dates}). Must be resolved before importing.`;
    }
    const next = dup.replaceable
      ? "You'll choose to skip it or replace the old bill when you continue."
      : 'It will be skipped; you can review why when you continue.';
    if (dup.overlapType === 'NEAR_EXACT') {
      return `Bill #${dup.existingBillId} already covers nearly the same dates (${dates}). ${next}`;
    }
    return `Bill #${dup.existingBillId} already covers these dates. ${next}`;
  }

  // --- Merge ---

  toggleMergeSelection(groupId: string): void {
    this.state.toggleMeterMergeSelection(groupId);
  }

  /** Total meter count across all LEN/SA (computed signal for template perf) */
  totalMeterCount = computed(() => {
    let count = 0;
    for (const len of this.legalEntities()) {
      for (const sa of len.serviceAddresses) {
        count += sa.meters.length;
      }
    }
    return count;
  });

  openMergeModal(): void {
    const mergeIds = this.mergeSelection();
    if (mergeIds.length < 2) return;

    const labelMap: Record<string, string> = {};
    const subLabelMap: Record<string, string> = {};
    // Hierarchy line per meter — "LEN name → SA address" — so the user
    // can tell which parent each meter belongs to before picking the
    // merge target.
    const response = this.state.matchResponse();
    if (response) {
      for (const len of response.legalEntities) {
        const lenName = this.state.getOriginalName(len.groupId);
        for (const sa of len.serviceAddresses) {
          const saAddress = this.state.getOriginalName(sa.groupId);
          for (const meter of sa.meters) {
            if (mergeIds.includes(meter.groupId)) {
              subLabelMap[meter.groupId] = `${lenName} → ${saAddress}`;
            }
          }
        }
      }
    }
    for (const groupId of mergeIds) {
      const acct = this.meterAccountNumbers[groupId];
      labelMap[groupId] = acct?.uan1 || this.state.getOriginalName(groupId);
    }

    this.mergeModalState = { isOpen: true, candidates: [...mergeIds], labelMap, subLabelMap };
  }

  closeMergeModal(): void {
    this.mergeModalState = { ...this.mergeModalState, isOpen: false };
  }

  onMergeConfirmed(targetGroupId: string): void {
    this.closeMergeModal();
    this.state.mergeMeters(targetGroupId);
    this.rebuildLookupMaps();
  }

  undoMerge(): void {
    this.state.undoLastMeterMerge();
    this.rebuildLookupMaps();
  }

  // Last-item delete confirmation
  showLastItemConfirm = false;

  deleteSelected(): void {
    if (this.state.wouldDeleteAll('meter')) {
      this.showLastItemConfirm = true;
      return;
    }
    this.state.deleteMeters();
    this.rebuildLookupMaps();
  }

  confirmLastItemDelete(): void {
    this.showLastItemConfirm = false;
    this.state.requestClose();
  }

  cancelLastItemDelete(): void {
    this.showLastItemConfirm = false;
  }

  undoDelete(): void {
    this.state.undoLastMeterDelete();
    this.rebuildLookupMaps();
  }

  // --- Navigation ---

  toggleEdit(groupId: string, event: Event): void {
    event.stopPropagation();
    const current = new Set(this.editingMeters());
    if (current.has(groupId)) {
      current.delete(groupId);
    } else {
      current.add(groupId);
      this.retryFailedUtilityLoad(groupId);
    }
    this.editingMeters.set(current);
  }

  /** Opening a meter for editing is an explicit user action — retry its utility fetch if it failed. */
  private retryFailedUtilityLoad(groupId: string): void {
    const ct = this.meterCommodityTypes[groupId];
    if (ct && this.utilityListsFailed.delete(ct)) {
      this.loadUtilitiesForMeter(groupId);
    }
  }

  isEditing(groupId: string): boolean {
    return this.editingMeters().has(groupId);
  }

  previewBill(meter: MeterMatchResult, event: Event): void {
    event.stopPropagation();
    if (meter.bills && meter.bills.length > 0) {
      const billGroup = meter.bills[0].groupId;
      const bill = this.state.getBillFileForGroup(billGroup);
      if (bill) {
        this.state.setPreviewBillFile(bill);
      }
    }
  }

  previewSpecificBill(bill: BillMatchResult, event: Event): void {
    event.stopPropagation();
    const billFile = this.state.getBillFileForGroup(bill.groupId);
    if (billFile) {
      this.state.setPreviewBillFile(billFile);
    }
  }

  togglePairChildren(groupId: string, event: Event): void {
    event.stopPropagation();
    this.billPairExpanded[groupId] = !this.billPairExpanded[groupId];
  }

  /** Previews the supplier half of a paired bill. */
  previewPairedSupplier(groupId: string, event: Event): void {
    event.stopPropagation();
    const info = this.billPairInfo[groupId];
    if (info) {
      this.state.setPreviewBillFile(info.supplierFile);
    }
  }

  goBack(): void {
    this.state.goBack();
  }

  goForward(): void {
    void this.continueFromMeters();
  }

  private async continueFromMeters(): Promise<void> {
    // Cross-entity duplicates block the step — resolve via migrate/edit/abort.
    if (this.anyMeterBlocked) return;

    // Field validation — the banner auto-shows whenever validationSummary has
    // errors, so we just rebuild and bail out if any are present.
    const result = this.runValidation();
    this.rebuildFieldErrors(result);
    if (result.hasBlockers) return;

    // Meters linked on this step enable their bill duplicate checks, so re-match before checking duplicates
    if (this.state.needsRematch()) {
      if (this.state.isMatching()) return;
      await this.state.runMatch(Number(this.state.selectedCustomerId()), { reuseRequest: true });
      if (this.state.error()) return;
    }

    // Check for blocking duplicates (PARTIAL, CONTAINS, CONTAINED_BY)
    const blocking = this.state.blockingDuplicates();
    if (blocking.length > 0) {
      this.blockingDuplicateError = `${blocking.length} bill${blocking.length !== 1 ? 's have' : ' has'} overlapping date ranges with existing bills. Please resolve them before proceeding.`;
      return;
    }
    this.blockingDuplicateError = null;

    // Exact duplicates: seed the Skip default, then let the user skip or replace each one
    if (this.state.duplicateDecisions().length > 0) {
      this.state.autoResolveDuplicates();
      this.showDuplicateModal.set(true);
      return;
    }

    // All clear - proceed
    void this.proceedToNextStep();
  }

  private runValidation(): StepValidationResult {
    const input: MeterValidationInput = {
      legalEntities: this.state.matchResponse()?.legalEntities ?? [],
      meterEdits: this.state.meterEdits(),
      isNewEntity: (gid) => this.state.isNewEntity(gid),
    };
    return validateMeterStep(input);
  }

  private rebuildFieldErrors(result: StepValidationResult): void {
    const maps = buildFieldErrorMaps(result);
    this.fieldErrors = maps.byField;
    this.hasEntityErrors = maps.byEntity;
    this.fieldWarnings = maps.byFieldWarn;
    this.validationSummary = maps.summary;
  }

  revalidate(): void {
    this.rebuildFieldErrors(this.runValidation());
  }

  private async proceedToNextStep(): Promise<void> {
    if (this.state.isMatching()) return; // re-entry guard: a re-match is already in flight
    // Committed selections enable meter-scoped bill duplicate checks —
    // re-fire the match before the analyzer so downstream warnings
    // (e.g. unapplied-match summary entries) reflect the new scope.
    if (this.state.needsRematch()) {
      await this.state.runMatch(Number(this.state.selectedCustomerId()), { reuseRequest: true });
      if (this.state.error()) return; // stay put — navigating would clear the error banner
    }
    this.state.completeStep('meter');
    this.state.analyzeFastTrack();
    this.state.buildBillNavigation();
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  // --- Duplicate Resolution Modal Handlers ---

  onDuplicateModalClose(open: boolean): void {
    this.showDuplicateModal.set(open);
  }

  onDuplicatesResolved(): void {
    this.showDuplicateModal.set(false);
    void this.proceedToNextStep();
  }

  onDuplicatesCancelled(): void {
    this.showDuplicateModal.set(false);
  }
}
